import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { Stars } from "@/components/Rating";
import { ReportReviewButton } from "@/components/ReportReviewButton";
import { BusinessVerifiedSocials } from "@/components/BusinessVerifiedSocials";
import { Loader2, ArrowLeft, MapPin, Tag, Globe, ShieldCheck } from "lucide-react";

export default function BusinessProfilePublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/business/${id}`).then((r) => setBiz(r.data)).catch(() => setBiz(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!biz) return <div className="min-h-screen bg-[#FAF9F6]"><Navbar /><p className="p-10 text-center text-zinc-500">Business not found.</p></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav" data-testid="business-profile-public">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" /> Back</button>

        <div className="rounded-3xl border border-zinc-200 bg-white p-6">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-zinc-100">
              {biz.logo ? <img src={fileUrl(biz.logo)} alt={biz.business_name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display text-2xl font-bold text-zinc-400">{biz.business_name?.[0]}</div>}
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold text-zinc-900" data-testid="business-name">{biz.business_name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-zinc-500">
                {biz.category && <span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> {biz.category}</span>}
                {biz.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {biz.location}</span>}
              </div>
              {biz.rating_count > 0 ? (
                <div className="mt-2 flex items-center gap-2" data-testid="business-rating">
                  <Stars value={biz.rating_avg} size={16} />
                  <span className="text-sm font-semibold text-zinc-900">{biz.rating_avg}</span>
                  <span className="text-sm text-zinc-400">({biz.rating_count} review{biz.rating_count === 1 ? "" : "s"})</span>
                </div>
              ) : (
                <p className="mt-2 text-sm text-zinc-400" data-testid="business-no-rating">No reviews yet</p>
              )}
            </div>
          </div>

          {biz.bio && <p className="mt-4 text-sm text-zinc-700">{biz.bio}</p>}
          {biz.website && <a href={biz.website.startsWith("http") ? biz.website : `https://${biz.website}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline"><Globe className="h-4 w-4" /> {biz.website}</a>}

          {biz.verified_socials?.length > 0 && (
            <div className="mt-4">
              <BusinessVerifiedSocials socials={biz.verified_socials} />
            </div>
          )}
        </div>

        <div className="mt-6 rounded-3xl border border-zinc-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-lg font-bold text-zinc-900">Reviews</h2>
            {biz.rating_count > 0 && <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> From verified collaborations</span>}
          </div>
          {biz.reviews?.length > 0 ? (
            <div className="mt-4 space-y-4">
              {biz.reviews.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-zinc-900">{r.reviewer_name}</p>
                      {r.verified && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Verified collaboration</span>}
                    </div>
                    <Stars value={r.rating} size={14} />
                  </div>
                  {r.created_at && <p className="mt-0.5 text-xs text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</p>}
                  {r.comment && <p className="mt-2 text-sm text-zinc-600">{r.comment}</p>}
                  <ReportReviewButton reviewId={r.id} />
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-zinc-400">No reviews yet.</p>
          )}
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
